import {
  criterionDefinitionForAssetType,
  criterionForDsFeature,
} from '../constants/preflight-criteria.js';
import type { PreflightPlatformCode } from '../constants/preflight-criteria.js';
import type { DsAssetResult, DsFeature } from './ds-response.js';

/** Five-state display vocabulary derived from the DS feature flags (KTD5 / KTD7-removed). */
export type FeatureResultState =
  | 'PASS'
  | 'FAIL'
  | 'PARTIAL'
  | 'NOT_WEIGHTED'
  | 'NOT_APPLICABLE'
  | 'NOT_ANALYZED';

/**
 * One DS sub-check: the rule that was evaluated, its verdict, and whatever the analyzer
 * measured. `name` is the rule ("Max words per frame"), `detail` the readout for this creative
 * ("Max words per frame: 36") — so the name is what several creatives can share and the detail
 * is what makes each of them different.
 */
export interface ProjectedSubCheck {
  name: string;
  /** Null when DS evaluated nothing for it. */
  passed: boolean | null;
  applicable: boolean;
  detail: string | null;
}

export interface ProjectedFeature {
  /** Stable catalogue code (null when the DS feature name doesn't resolve). */
  code: string | null;
  name: string;
  definition: string | null;
  displayOrder: number | null;
  /** DS-emitted weight (authoritative — Nexus owns no weights). */
  weight: number;
  featureScore: number | null;
  state: FeatureResultState;
  subChecks: ProjectedSubCheck[];
}

function classifyFeature(feature: DsFeature): FeatureResultState {
  if (feature.weight === 0) return 'NOT_WEIGHTED';
  if (!feature.is_scored) return 'NOT_APPLICABLE';
  if (feature.feature_score == null) return 'NOT_ANALYZED';
  if (feature.feature_score >= 1) return 'PASS';
  if (feature.feature_score <= 0) return 'FAIL';
  return 'PARTIAL';
}

/** States that carry no pass/fail verdict and don't contribute to the score. */
const EXCLUDED_STATES: ReadonlySet<FeatureResultState> = new Set([
  'NOT_WEIGHTED',
  'NOT_APPLICABLE',
  'NOT_ANALYZED',
]);

/** Whether a raw feature state carries no verdict (excluded from the score). */
export function isExcludedState(state: FeatureResultState): boolean {
  return EXCLUDED_STATES.has(state);
}

/**
 * Effective result state for DISPLAY. Unweighted features (NOT_WEIGHTED) are excluded from
 * the score, but DS still evaluates many of them — when a featureScore is present we surface
 * PASS/FAIL/PARTIAL instead of the muted "—". Thresholds mirror {@link classifyFeature} so an
 * unweighted-but-scored feature renders like every other feature. Every other state is unchanged.
 *
 * Display concern only: scoring, pass/fail counts, and the active/inactive grouping must keep
 * switching on the raw `feature.state`, never on this. Shared by the web UI, the client-facing
 * text/CSV/XLSX/JSON exports, and the server-side PDF so they cannot drift (AD-1749/AD-1723).
 */
export function featureDisplayState(
  feature: Pick<ProjectedFeature, 'state' | 'featureScore'>,
): FeatureResultState {
  if (feature.state === 'NOT_WEIGHTED' && feature.featureScore != null) {
    if (feature.featureScore >= 1) return 'PASS';
    if (feature.featureScore <= 0) return 'FAIL';
    return 'PARTIAL';
  }
  return feature.state;
}

/**
 * Whether a criterion is shown to users: its DISPLAY state carries a verdict
 * (PASS/FAIL/PARTIAL). Criteria whose display state is still excluded — NOT_WEIGHTED with no
 * score, NOT_APPLICABLE, NOT_ANALYZED — render no result and are hidden everywhere.
 */
export function isFeatureVisible(
  feature: Pick<ProjectedFeature, 'state' | 'featureScore'>,
): boolean {
  return !isExcludedState(featureDisplayState(feature));
}

/** The criteria users actually see, in input order (see {@link isFeatureVisible}). */
export function visibleFeatures(features: ProjectedFeature[]): ProjectedFeature[] {
  return features.filter(isFeatureVisible);
}

/**
 * The analyzer's readouts for a criterion, as the notes column, the exports and the PDF all
 * show them. One definition so a note in the app reads the same as a note in a client's CSV.
 */
export function featureDetails(feature: Pick<ProjectedFeature, 'subChecks'>): string[] {
  return feature.subChecks
    .map((sub) => sub.detail)
    .filter((detail): detail is string => detail != null && detail.trim() !== '');
}

/**
 * Project the DS `features[]` tree into the per-criterion read model (runs on read
 * over `report_asset.rawPayload`). Joins each feature to the static catalogue by code
 * for its definition + display order; unknown features surface with null metadata.
 */
export function projectFeatures(result: DsAssetResult): ProjectedFeature[] {
  const platform = result.platform as PreflightPlatformCode;
  return result.features
    .map((feature): ProjectedFeature => {
      const def = criterionForDsFeature(feature.name, platform);
      return {
        code: def?.code ?? null,
        name: feature.name,
        definition: criterionDefinitionForAssetType(def, result.asset_type),
        displayOrder: def?.displayOrder ?? null,
        weight: feature.weight,
        featureScore: feature.feature_score,
        state: classifyFeature(feature),
        subChecks: (feature.sub_checks ?? []).map((sub) => ({
          name: sub.name,
          passed: sub.passed,
          applicable: sub.applicable,
          detail: sub.detail ?? null,
        })),
      };
    })
    .sort(
      (a, b) =>
        (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
        (b.displayOrder ?? Number.MAX_SAFE_INTEGER),
    );
}
